<template>
    <div class="modal-status-form-step">
        <div class="modal-status-form-step__counter">
            {{ currentStepIndex + 1 }}/{{ steps.length }}
        </div>
        <div class="modal-status-form-step__title"
            v-text="currentStep.header"
        />
        <div class="modal-status-form-step__description"
            v-text="currentStep.description"
        />
        <form class="modal-status-form-step__form"
            v-on:submit.prevent="submitHandler"
            >
            <div class="modal-status-form-step__form-field"
                v-for="(field, fieldIndex) in fields"
                :key="fieldIndex + field.id"
                >
                <modal-status-form-image
                    v-if="field.type === 'ImgField'"
                    v-bind:field="field"
                    v-on:validation-update="validationUpdateHandler"
                />
                <modal-status-form-field
                    v-else
                    v-bind:field="field"
                    v-on:validation-update="validationUpdateHandler"
                />
            </div>
        </form>
        <div class="modal-status-form-step__buttons">
            <div class="modal-status-form-step__buttons-item">
                <ui-button
                    v-on:click="submitHandler"
                    v-bind:disabled="$store.state.status.isTouched && !isValid"
                    >
                    Далее
                </ui-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
    name: 'modal-status-form-step',
    data: () => ({
        validFields: [],
        isBankInfoChecked: false,
        bikCheckTimeoutId: null,
    }),
    computed: {
        ...mapGetters('status', [
            'steps',
            'currentStep',
            'currentBik',
            'currentLs',
        ]),
        ...mapState('status', [
            'currentStepIndex',
            'estateId',
            'getFormDataIsLoading',
            'createFormDataIsLoading',
            'updateFormDataError',
            'getBankInfoResult',
        ]),
        isValid() {
            return this.fields.every((field) => this.validFields.some((validField) => validField.id === field.id && validField.isValid));
        },
        fields() {
            return this.currentStep.fields.filter((field) => (field.visible || field.visible === undefined) && !field.isHidden);
        },
        bikField() {
            const bikFieldId = 'bik';
            return this.fields.find(x => x.id === bikFieldId) || null;
        },
    },
    methods: {
        validationUpdateHandler({ id, isValid }) {
            const index = this.validFields.findIndex((field) => field.id === id);
            if (isValid && index === -1) {
                this.validFields.push({ id, isValid });
            } else if (!isValid && index !== -1) {
                this.validFields.splice(index, 1);
            }
        },
        submitHandler() {
            if (!this.isValid) {
                this.isBankInfoChecked = false;
                this.$store.state.status.isTouched = true;
                return;
            }
            const formData = {
                values: {},
            };
            this.fields.forEach((field) => {
                const value = this.$store.state.status.newValues[field.id];
                if (value !== undefined && field.isDisabled !== true) {
                    formData.values[field.id] = value;
                }
            });
            if (Object.keys(formData.values).length === 0) {
                this.goNextHandler();
                return;
            }
            this.$store.dispatch('status/updateFormData', {
                id: this.estateId,
                formData,
                onSuccess: () => {
                    this.$store.dispatch('status/getFormData', {
                        onSuccess: this.goNextHandler,
                    });
                },
            });
        },
        goNextHandler() {
            const nextStepIndex = Math.min(this.currentStepIndex + 1, this.steps.length - 1);
            if (nextStepIndex === this.steps.length - 1) {
                this.$emit('completed');
            } else {
                this.$store.state.status.currentStepIndex = nextStepIndex;
            }
        },
        checkBik() {
            if (this.bikField && !this.isBankInfoChecked) {
                this.$store.dispatch('status/getBankInfo', {
                    bik: this.currentBik,
                });
            }
        },
        debouncedBikCheck() {
            this.isBankInfoChecked = false;
            clearTimeout(this.bikCheckTimeoutId);
            this.bikCheckTimeoutId = setTimeout(() => {
                this.checkBik();
            }, 500);
        },
    },
    watch: {
        bikField(newVal, oldVal) {
            if (newVal && oldVal === null && !this.getBankInfoResult) {
                this.checkBik();
            }
        },
        currentStepIndex() {
            this.$store.state.status.isTouched = false;
            this.validFields = [];
        },
        currentBik() {
            this.debouncedBikCheck();
        },
        currentLs() {
            this.$store.state.status.updateFormDataError = null;
        },
        updateFormDataError() {
            this.$store.state.status.isTouched = true;
        },
        getBankInfoResult(newVal) {
            if (!newVal) {
                return;
            }
            this.isBankInfoChecked = true;
            if (Object.keys(this.getBankInfoResult).some(x => this.getBankInfoResult[x] === null)) {
                this.$store.state.status.getBankInfoError = {};
                return;
            }
            const keys = Object.keys(this.getBankInfoResult);
            keys.forEach(key => {
                const relatedField = this.$store.getters['status/currentStep'].fields.find(x => x.id === key);
                if (relatedField && relatedField.value !== this.getBankInfoResult[key]) {
                    this.$store.state.status.newValues[key] = this.getBankInfoResult[key];
                    relatedField.value = this.getBankInfoResult[key];
                }
            });
            this.$store.getters['status/currentStep'].fields = this.$store.getters['status/currentStep'].fields.map((field) => field);
        },
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.modal-status-form-step {
    &__counter {
        .typography-caption();

        width: fit-content;
        padding: 4px 12px;
        border-radius: 20px;
        margin-bottom: 32px;

        font-weight: bold;

        background-color: @color-gray-light;
    }
    &__title {
        .typography-body-lg();

        margin-bottom: 8px;

        font-weight: bold;
    }
    &__description {
        .typography-small();

        margin-bottom: 32px;
    }
    &__form {
        margin-bottom: 32px;
        &-field {
            & + & {
                margin-top: 32px;
            }
        }
    }
    &__buttons {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 16px;
        &-item {
            flex: 0 0 auto;

            min-width: 206px;
        }
    }
    @media @media-lg-down {
        &__counter {
            margin-left: auto;
            margin-right: auto;
        }
    }
}
</style>
